/**
 * /settings — one page. This project's tabs come first: Project (default:
 * name, description, URL, leave, delete) and Members (`?tab=members`). Then
 * Connections (`?tab=connections`) and Run credentials (`?tab=credentials`),
 * which are shared by all projects and say so above their content. An
 * annotator sees only Project (to leave). Capped in width, so forms and
 * tables don't stretch across a wide screen.
 * The old `/settings/connections` and `/settings/credentials` links land on
 * the matching tab, keeping `?add=1`.
 */

import type { ComponentType } from "react";
import { Navigate, useLocation } from "react-router";
import { Globe } from "lucide-react";
import { PageHeader } from "@/components/ui/page";
import { useUrlState } from "@/lib/use-url-state";
import { useProject } from "@/features/projects/hooks";
import { ConnectionsTab } from "./connections-tab";
import { CredentialsTab } from "./credentials-tab";
import { MembersTab } from "./members-tab";
import { ProjectTab } from "./project-tab";

type Tab = "project" | "members" | "connections" | "credentials";

const TABS: Record<Tab, { label: string; body: ComponentType; shared?: boolean }> = {
  project: { label: "Project", body: ProjectTab },
  members: { label: "Members", body: MembersTab },
  connections: { label: "Connections", body: ConnectionsTab, shared: true },
  credentials: { label: "Run credentials", body: CredentialsTab, shared: true },
};

// `Object.hasOwn`, so `?tab=constructor` is not a tab.
const isTab = (t: string | null): t is Tab => t !== null && Object.hasOwn(TABS, t);

/** The tabs someone may open: members need `members:read`; the shared ones are not an annotator's business. */
function useVisibleTabs(): Tab[] {
  const { project, can } = useProject();
  if (project?.role === "annotator") return ["project"];
  return ["project", ...(can("members:read") ? (["members"] as const) : []), "connections", "credentials"];
}

function TabButton({ tab, active, onPick }: { tab: Tab; active: boolean; onPick: (t: Tab) => void }) {
  return (
    <button type="button" role="tab" className="tab" aria-selected={active} onClick={() => onPick(tab)}>
      {TABS[tab].label}
    </button>
  );
}

export function SettingsPage() {
  const url = useUrlState();
  const { project } = useProject();
  const visible = useVisibleTabs();
  const raw = url.get("tab");
  const tab: Tab = isTab(raw) && visible.includes(raw) ? raw : "project";
  const Body = TABS[tab].body;
  // A tab switch also closes the other tab's add dialog.
  const pick = (t: Tab) => url.set({ tab: t === "project" ? null : t, add: null });
  return (
    <div className="flex max-w-[860px] flex-col gap-[18px]">
      <PageHeader title="Settings" subtitle={`Settings for ${project?.name ?? "this project"}.`} />
      <div
        role="tablist"
        aria-label="Settings sections"
        className="flex items-end gap-0.5 border-b border-line"
      >
        {visible.map((t) => (
          <TabButton key={t} tab={t} active={t === tab} onPick={pick} />
        ))}
      </div>
      {TABS[tab].shared && (
        <p className="flex items-center gap-1.5 text-[13px] text-muted">
          <Globe size={13} className="shrink-0 text-faint" aria-hidden />
          Shared by all projects. A change here applies to every project.
        </p>
      )}
      <Body />
    </div>
  );
}

/** An old `/settings/<tab>` link: the same query string, on the settings page's tab. */
export function SettingsRedirect({ tab }: { tab: Tab }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set("tab", tab);
  const query = params.toString();
  return <Navigate to={`/settings${query ? `?${query}` : ""}`} replace />;
}
